/**
 * Centralized API client with fetch wrapper and SSE support
 */

function buildApiError(endpoint, response, bodySnippet) {
    const err = new Error(`${endpoint} → ${response.status} ${response.statusText}${bodySnippet ? `: ${bodySnippet}` : ''}`);
    err.status = response.status;
    err.url = endpoint;
    return err;
}

async function readSnippet(response) {
    try {
        const text = await response.text();
        return text.length > 200 ? text.slice(0, 200) + '…' : text;
    } catch {
        return '';
    }
}

/**
 * Make a GET request
 */
export async function get(endpoint) {
    let response;
    try {
        response = await fetch(endpoint);
    } catch (networkErr) {
        // Network/CORS/DNS/abort. Attach URL so callers see what failed.
        const err = new Error(`${endpoint} → ${networkErr.message || 'network error'}`);
        err.url = endpoint;
        err.cause = networkErr;
        throw err;
    }
    if (!response.ok) {
        throw buildApiError(endpoint, response, await readSnippet(response));
    }
    return response.json();
}

/**
 * Make a POST request
 */
export async function post(endpoint, data = null, options = {}) {
    const config = {
        method: 'POST',
        ...options
    };

    if (data) {
        if (data instanceof FormData) {
            config.body = data;
        } else {
            config.headers = {
                'Content-Type': 'application/json',
                ...options.headers
            };
            config.body = JSON.stringify(data);
        }
    }

    const response = await fetch(endpoint, config);
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    return response.json();
}

/**
 * Make a PUT request
 */
export async function put(endpoint, data = null) {
    const config = {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json'
        }
    };

    if (data) {
        config.body = JSON.stringify(data);
    }

    const response = await fetch(endpoint, config);
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    return response.json();
}

/**
 * Make a DELETE request
 */
export async function del(endpoint) {
    const response = await fetch(endpoint, { method: 'DELETE' });
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    return response.json();
}

/**
 * Create an EventSource for Server-Sent Events
 * @param {string} url - The SSE endpoint URL
 * @param {object} handlers - Event handlers { onMessage, onError, onOpen }
 * @returns {EventSource} The EventSource instance
 */
export function createEventSource(url, handlers = {}) {
    const eventSource = new EventSource(url);

    if (handlers.onOpen) {
        eventSource.onopen = handlers.onOpen;
    }

    if (handlers.onMessage) {
        eventSource.onmessage = handlers.onMessage;
    }

    if (handlers.onError) {
        eventSource.onerror = handlers.onError;
    }

    return eventSource;
}

/**
 * Fetch raw response (for non-JSON responses)
 */
export async function fetchRaw(endpoint, options = {}) {
    const response = await fetch(endpoint, options);
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    return response;
}

// Export as API object for convenient use
export const API = {
    get,
    post,
    put,
    delete: del,
    createEventSource,
    fetchRaw
};
